import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, FileText, Users, Building2, Loader2 } from 'lucide-react';
import { useAuth, API } from '../context/AuthContext';

const STATUS_LABELS = {
  pending: 'Pendiente',
  open: 'Abierto',
  in_progress: 'En proceso',
  resolved: 'Resuelto',
  closed: 'Cerrado'
};

const GlobalSearch = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setResults(null);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/search`, { params: { q: query.trim() } });
        setResults(res.data);
        setOpen(true);
      } catch {
        setResults(null);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const goTo = (path) => {
    setOpen(false);
    setQuery('');
    navigate(path);
  };

  const buildingsPath = user?.role === 'super_admin' ? '/super-admin/conjuntos' : '/admin/conjuntos';
  const total = results ? results.residents.length + results.pqrs.length + results.buildings.length : 0;

  return (
    <div ref={containerRef} className="relative flex-1 max-w-md hidden sm:block">
      <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full border border-slate-200 shadow-sm focus-within:ring-2 focus-within:ring-secondary/30 transition-shadow">
        {loading
          ? <Loader2 className="h-4 w-4 text-slate-400 animate-spin shrink-0" />
          : <Search className="h-4 w-4 text-slate-400 shrink-0" />
        }
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => { if (results && query.trim().length >= 2) setOpen(true); }}
          placeholder="Buscar residentes, PQRS, conjuntos..."
          data-testid="global-search-input"
          className="w-full bg-transparent text-sm text-slate-700 placeholder:text-slate-400 outline-none"
        />
        <kbd className="hidden lg:inline-flex items-center gap-0.5 text-[10px] font-medium text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded shrink-0">
          Ctrl K
        </kbd>
      </div>

      {open && results && (
        <div
          data-testid="search-results-dropdown"
          className="absolute top-full mt-2 w-full bg-white rounded-2xl border border-slate-100 shadow-2xl overflow-hidden z-50 max-h-[420px] overflow-y-auto"
        >
          {total === 0 ? (
            <p className="p-4 text-sm text-slate-400 text-center" data-testid="search-no-results">
              Sin resultados para "{results.query}"
            </p>
          ) : (
            <>
              {results.residents.length > 0 && (
                <div className="py-2">
                  <p className="px-4 py-1 text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Residentes</p>
                  {results.residents.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => goTo('/residents')}
                      data-testid={`search-result-resident-${r.id}`}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-left transition-colors"
                    >
                      <div className="h-8 w-8 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                        <Users className="h-4 w-4 text-blue-500" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-700 truncate">{r.name}</p>
                        <p className="text-xs text-slate-400 truncate">{r.email}{r.building_name ? ` · ${r.building_name}` : ''}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {results.pqrs.length > 0 && (
                <div className="py-2 border-t border-slate-50">
                  <p className="px-4 py-1 text-[10px] font-semibold text-slate-400 uppercase tracking-widest">PQRS</p>
                  {results.pqrs.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => goTo('/pqrs')}
                      data-testid={`search-result-pqrs-${p.id}`}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-left transition-colors"
                    >
                      <div className="h-8 w-8 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                        <FileText className="h-4 w-4 text-amber-500" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-700 truncate">{p.title}</p>
                        <p className="text-xs text-slate-400 truncate">
                          {STATUS_LABELS[p.status] || p.status}{p.building_name ? ` · ${p.building_name}` : ''}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {results.buildings.length > 0 && (
                <div className="py-2 border-t border-slate-50">
                  <p className="px-4 py-1 text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Conjuntos</p>
                  {results.buildings.map((b) => (
                    <button
                      key={b.id}
                      onClick={() => goTo(buildingsPath)}
                      data-testid={`search-result-building-${b.id}`}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-left transition-colors"
                    >
                      <div className="h-8 w-8 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                        <Building2 className="h-4 w-4 text-emerald-500" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-700 truncate">{b.name}</p>
                        <p className="text-xs text-slate-400 truncate">{b.address}{b.city ? `, ${b.city}` : ''}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
